import {
    addDaysKey, buildRule, describeRule, firstOccurrenceKey, formatDay, keyParts, keyToInstant,
    makeKey, weekdayOfKey, WEEKDAYS_RULE, type RepeatFreq, type RepeatRule
} from "./taskModel";
import { todayKey } from "./taskModel";

/**
 * Local, rule-based natural-language quick add (no AI):
 *   "Call bank tomorrow 5pm"        → due tomorrow 17:00 + reminder
 *   "Pay rent every month on 1st"   → repeats monthly on the 1st, due the next 1st
 *   "Submit report fri !"           → due Friday, important
 * Each recognised part becomes a chip; dismissing a chip re-parses with that
 * kind ignored so its words stay in the title.
 */

export type ChipKind = "date" | "repeat" | "important";

export interface QuickAddChip {
    kind: ChipKind;
    label: string;
}

export interface ParsedQuickAdd {
    title: string;
    dueAt: string | null;
    reminderAt: string | null;
    recurrence: string | null;
    isImportant: boolean;
    chips: QuickAddChip[];
}

const DAY = "(mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)(?:day|nesday|sday|rsday|urday)?";
const DAY_TO_CODE: Record<string, string> = { mon: "MO", tue: "TU", wed: "WE", thu: "TH", fri: "FR", sat: "SA", sun: "SU" };
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const MONTH = "(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*";
const PREFIX = "(?:(?:on|by|due)\\s+)?";

const codeOf = (word: string) => DAY_TO_CODE[word.slice(0, 3).toLowerCase()] ?? "MO";
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

export function parseQuickAdd(
    input: string,
    tz: string,
    ignore: ReadonlySet<ChipKind> = new Set(),
    now: Date = new Date()
): ParsedQuickAdd {
    let text = ` ${input} `;
    const take = (re: RegExp): RegExpExecArray | null => {
        const m = re.exec(text);
        if (!m) return null;
        text = `${text.slice(0, m.index)} ${text.slice(m.index + m[0].length)}`;
        return m;
    };
    const today = todayKey(tz);
    const chips: QuickAddChip[] = [];

    // ── Importance ──
    let isImportant = false;
    if (!ignore.has("important")) {
        if (take(/\s!+(?=\s)/)) isImportant = true;
        if (/!+\s*$/.test(text)) {
            text = text.replace(/!+\s*$/, " ");
            isImportant = true;
        }
    }

    // ── Repeat ──
    let rule: RepeatRule | null = null;
    if (!ignore.has("repeat")) {
        const setRule = (freq: RepeatFreq, interval = 1, byDay: string[] = []) => {
            rule = { freq, interval, byDay, byMonthDay: null };
        };
        let m: RegExpExecArray | null;
        if (take(/\b(?:every\s+)?(?:weekdays?|work\s*days?)\b/i)) {
            rule = { freq: "WEEKLY", interval: 1, byDay: ["MO", "TU", "WE", "TH", "FR"], byMonthDay: null };
        } else if ((m = take(/\bevery\s+(other|\d{1,3})\s+(day|week|month|year)s?\b/i))) {
            const n = m[1]?.toLowerCase() === "other" ? 2 : Math.max(1, Number(m[1]));
            setRule(unitFreq(m[2] ?? "day"), n);
        } else if ((m = take(new RegExp(`\\bevery\\s+(${DAY}(?:\\s*(?:,|and|&)\\s*${DAY})*)\\b`, "i")))) {
            const codes = Array.from((m[1] ?? "").matchAll(new RegExp(DAY, "gi")), (x) => codeOf(x[0]));
            setRule("WEEKLY", 1, Array.from(new Set(codes)));
        } else if ((m = take(/\b(?:every\s+(day|week|month|year)|(daily|weekly|monthly|yearly|annually))\b/i))) {
            const word = (m[1] ?? m[2] ?? "day").toLowerCase();
            setRule(word === "annually" ? "YEARLY" : unitFreq(word.replace(/ly$/, "").replace(/^dai$/, "day")));
        }
        const r = rule as RepeatRule | null;
        if (r?.freq === "MONTHLY") {
            const md = take(/\b(?:on\s+)?(?:the\s+)?(\d{1,2})(?:st|nd|rd|th)\b/i) ?? take(/\bon\s+(?:the\s+)?(\d{1,2})\b/i);
            const d = Number(md?.[1]);
            if (d >= 1 && d <= 31) r.byMonthDay = d;
        } else if (r?.freq === "WEEKLY" && r.byDay.length === 0) {
            const on = take(new RegExp(`\\bon\\s+${DAY}\\b`, "i"));
            if (on?.[1]) r.byDay = [codeOf(on[1])];
        }
    }

    // ── Time + date ──
    let time: { h: number; m: number } | null = null;
    let dateKey: string | null = null;
    if (!ignore.has("date")) {
        let m: RegExpExecArray | null;
        if ((m = take(/\b(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)\b/i))) {
            const h = Number(m[1]) % 12 + (m[3]?.toLowerCase() === "pm" ? 12 : 0);
            time = { h, m: Number(m[2] ?? 0) };
        } else if ((m = take(/\bat\s+(\d{1,2})(?::(\d{2}))?\b/i))) {
            const h = Number(m[1]);
            time = { h: h >= 1 && h <= 7 ? h + 12 : h, m: Number(m[2] ?? 0) };
        } else if ((m = take(/\b(\d{1,2}):(\d{2})\b/))) {
            time = { h: Number(m[1]), m: Number(m[2]) };
        } else if (take(/\b(?:at\s+)?(?:noon|midday)\b/i)) {
            time = { h: 12, m: 0 };
        }
        if (time && (time.h > 23 || time.m > 59)) time = null;

        const { y: ty, m: tm } = keyParts(today);
        const todayWd = weekdayOfKey(today);
        if (take(new RegExp(`\\b${PREFIX}(?:the\\s+)?day\\s+after\\s+tomorrow\\b`, "i"))) {
            dateKey = addDaysKey(today, 2);
        } else if ((m = take(new RegExp(`\\b${PREFIX}(today|tonight)\\b`, "i")))) {
            dateKey = today;
            if (m[1]?.toLowerCase() === "tonight" && !time) time = { h: 20, m: 0 };
        } else if (take(new RegExp(`\\b${PREFIX}(?:tomorrow|tomorow|tmrw|tmr)\\b`, "i"))) {
            dateKey = addDaysKey(today, 1);
        } else if (take(/\bnext\s+week\b/i)) {
            dateKey = addDaysKey(today, 8 - todayWd);
        } else if (take(/\bnext\s+month\b/i)) {
            dateKey = makeKey(ty, tm + 1, 1);
        } else if ((m = take(/\bin\s+(\d{1,3})\s+(day|week|month)s?\b/i))) {
            const n = Number(m[1]);
            const unit = (m[2] ?? "day").toLowerCase();
            if (unit === "month") {
                const { d } = keyParts(today);
                const y = ty + Math.floor((tm - 1 + n) / 12);
                const mo = ((tm - 1 + n) % 12) + 1;
                dateKey = makeKey(y, mo, Math.min(d, daysIn(y, mo)));
            } else {
                dateKey = addDaysKey(today, unit === "week" ? n * 7 : n);
            }
        } else if ((m = take(/\b(\d{4})-(\d{2})-(\d{2})\b/))) {
            dateKey = makeKey(Number(m[1]), Number(m[2]), Number(m[3]));
        } else if ((m = take(new RegExp(`\\b${PREFIX}(\\d{1,2})(?:st|nd|rd|th)?\\s+${MONTH}\\b`, "i")))) {
            dateKey = upcomingDate(today, MONTHS.indexOf((m[2] ?? "").toLowerCase()) + 1, Number(m[1]));
        } else if ((m = take(new RegExp(`\\b${PREFIX}${MONTH}\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, "i")))) {
            dateKey = upcomingDate(today, MONTHS.indexOf((m[1] ?? "").toLowerCase()) + 1, Number(m[2]));
        } else if ((m = take(new RegExp(`\\b${PREFIX}(\\d{1,2})\\/(\\d{1,2})(?:\\/(\\d{2,4}))?\\b`)))) {
            // Day first (India / UK).
            const d = Number(m[1]);
            const mo = Number(m[2]);
            if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
                const yRaw = m[3] ? Number(m[3]) : null;
                dateKey = yRaw ? makeKey(yRaw < 100 ? 2000 + yRaw : yRaw, mo, d) : upcomingDate(today, mo, d);
            }
        } else if ((m = take(new RegExp(`\\b(?:(on|by|due|this|next)\\s+)?${DAY}\\b`, "i")))) {
            const target = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"].indexOf(codeOf(m[2] ?? "mon")) + 1;
            let diff = (target - todayWd + 7) % 7 || 7;
            if (m[1]?.toLowerCase() === "next" && todayWd + diff <= 7) diff += 7;
            dateKey = addDaysKey(today, diff);
        } else if ((m = take(/\b(?:on\s+)?the\s+(\d{1,2})(?:st|nd|rd|th)\b/i))) {
            const d = Number(m[1]);
            const { d: td } = keyParts(today);
            if (d >= 1 && d <= 31) {
                dateKey = d >= td ? makeKey(ty, tm, Math.min(d, daysIn(ty, tm)))
                    : makeKey(tm === 12 ? ty + 1 : ty, tm === 12 ? 1 : tm + 1, d);
            }
        }
    }

    const finalRule = rule as RepeatRule | null;
    if (finalRule && !dateKey) dateKey = firstOccurrenceKey(finalRule, today);
    if (time && !dateKey) {
        dateKey = keyToInstant(today, tz, time.h, time.m).getTime() > now.getTime() ? today : addDaysKey(today, 1);
    }
    const dueAt = dateKey ? keyToInstant(dateKey, tz, time?.h ?? 0, time?.m ?? 0).toISOString() : null;
    const reminderAt = time && dueAt ? dueAt : null;
    const recurrence = finalRule ? buildRule(finalRule) : null;

    if (isImportant) chips.push({ kind: "important", label: "Important" });
    if (dueAt) chips.push({ kind: "date", label: `Due ${formatDay(dueAt, tz, today)}${reminderAt ? " · reminder" : ""}` });
    if (recurrence) {
        const text = describeRule(recurrence);
        chips.push({
            kind: "repeat",
            label: recurrence === WEEKDAYS_RULE ? "Repeats on weekdays" : `Repeats ${text.charAt(0).toLowerCase()}${text.slice(1)}`
        });
    }

    let title = text.replace(/\s+/g, " ").trim();
    for (let i = 0; i < 3; i++) {
        title = title.replace(/^(?:on|at|by|due|every|and|,)\s+/i, "").replace(/\s+(?:on|at|by|due|every|and|,)$/i, "").trim();
    }
    if (!title) title = input.trim();
    return { title, dueAt, reminderAt, recurrence, isImportant, chips };
}

function unitFreq(unit: string): RepeatFreq {
    switch (unit.toLowerCase()) {
        case "week": return "WEEKLY";
        case "month": return "MONTHLY";
        case "year": return "YEARLY";
        default: return "DAILY";
    }
}

/** month/day this year, or next year when that date has passed. */
function upcomingDate(today: string, month: number, day: number): string | null {
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const { y } = keyParts(today);
    const k = makeKey(y, month, Math.min(day, daysIn(y, month)));
    return k >= today ? k : makeKey(y + 1, month, Math.min(day, daysIn(y + 1, month)));
}
